import type { OnInit } from '@angular/core';
import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, Output } from '@angular/core';

import { ButtonV3Component } from 'components/atoms/button-v3/button-v3.component';
import type { IButtonStyleProperties } from 'components/atoms/button-v3/entities/types';
import { ImageComponent } from 'components/atoms/image/image.component';
import { AbstractWebsiteComponent } from 'components/molecules/abstract-website-component/abstract-website-component';
import type { ModalControl } from 'components/organisms/modal';
import { MODAL_CONTROL } from 'components/organisms/modal';
import { ButtonV3Service } from 'src/services/button/button-v3.service';

import { imageConfig } from './entities/const';
import type { IDialogContextData, IDialogStrings, TDialogButtonsIdentifiers } from './entities/types';

@Component({
    selector: 'website-dialog',
    templateUrl: './dialog.component.html',
    styleUrls: ['./dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [ButtonV3Component, ImageComponent],
})
export class DialogComponent extends AbstractWebsiteComponent implements OnInit {
    readonly modalControl = inject<ModalControl<IDialogContextData>>(MODAL_CONTROL, { optional: true });
    private readonly buttonV3Service = inject(ButtonV3Service);

    public get COMPONENT_NAME() {
        return 'website-dialog' + (this.dialogTestId ? `-${this.dialogTestId}` : '');
    }

    @Input()
    public dialogTestId?: string;
    public buttonStyleProperties: IButtonStyleProperties;
    public secondaryButtonStyleProperties: IButtonStyleProperties;
    public defaultImage = 'tp/static/assets/img/vector/icon-evo--user-key.svg';
    public readonly imageConfig = imageConfig;

    @Input() public strings: IDialogStrings;
    @Input() public isSecondaryButtonHidden = false;
    @Output() public readonly receiveAnswer = new EventEmitter<TDialogButtonsIdentifiers>();

    public ngOnInit(): void {
        if (this.modalControl) {
            this.strings = this.modalControl.contextData.strings;
            this.isSecondaryButtonHidden = this.modalControl.contextData.isSecondaryButtonHidden;
            this.dialogTestId = this.modalControl.contextData.dialogTestId;
        }
        this.buttonStyleProperties = this.buttonV3Service.getCallToActionButtonStyleProperties(
            { size: 'small', priority: 'tertiary', isShinyAnimatedButton: false },
            'white'
        );
        this.secondaryButtonStyleProperties = this.buttonV3Service.getCallToActionButtonStyleProperties(
            { size: 'small', priority: 'primary', isShinyAnimatedButton: false },
            'white'
        );
    }

    public handleAnswer(buttonIdentifier: TDialogButtonsIdentifiers): void {
        if (this.modalControl && !this.isHandleInVideoPopup()) {
            this.modalControl.close(buttonIdentifier);
        }

        this.receiveAnswer.emit(buttonIdentifier);
    }

    public handleClose(): void {
        this.modalControl?.close();
    }

    private isHandleInVideoPopup(): boolean {
        return this.modalControl
            ? !!('wistiaId' in this.modalControl.contextData && this.modalControl.contextData.wistiaId)
            : false;
    }
}
